import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import ActivityApi from '../api/instance/ActivityApi';
import DiskApi from '../api/host/DiskApi';
import HostApi from '../api/host/HostApi';
import StatisticApi from '../api/instance/StatisticApi';
import TagApi from '../api/TagApi';
import { buildApiQuery, getConvertedTimeToUTC, resolveUserTimezone } from '../helpers/timeRangeUtils';
import { INTERVAL_MINUTE } from '../component/Constants';
import { AlertRuleFormData } from '../types/Alerts/AlertRuleFormState';
import HostDisk from '../types/host/HostDisk';
import HostTarget from '../types/host/HostTarget';
import InstanceTarget from '../types/instance/InstanceTarget';
import Tags from '../types/Tags';
import {
    AlertRulePreviewEntity,
    getAlertRulePreviewRowValue,
    getAlertRuleTriggeredCounts,
    getAlertRulePreviewEntities
} from '../helpers/AlertRulePreviewUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

export type AlertRulePreviewEntityType = 'host' | 'datasource' | 'disk';

// What the rule's scope resolves to, for the "Matching …" wording.
export const ENTITY_LABELS: Record<AlertRulePreviewEntityType, string> = {
    host: 'hosts',
    datasource: 'instances',
    disk: 'disks'
};

export interface AlertRulePreviewPoint {
    x: number;
    y: number;
}

export interface AlertRulePreviewState {
    entityType: AlertRulePreviewEntityType;
    matchedEntities: AlertRulePreviewEntity[];
    selectedEntityId?: number | string;
    selectedEntity?: AlertRulePreviewEntity;
    setSelectedEntityId: (id: number | string) => void;
    series: AlertRulePreviewPoint[];
    loading: boolean;
    error: string | null;
    emptyReason: string | null;
    previewFrom: dayjs.Dayjs;
    previewTo: dayjs.Dayjs;
}

const getLastPointValue = (points: AlertRulePreviewPoint[]): number | undefined => {
    const lastPoint = [...points].reverse().find(point => point.y !== null && point.y !== undefined && !Number.isNaN(point.y));
    return lastPoint?.y;
};

const getMaxPointValue = (points: AlertRulePreviewPoint[]): number | undefined => {
    const values = points
        .map(point => point.y)
        .filter(value => value !== null && value !== undefined && !Number.isNaN(value));

    return values.length > 0 ? Math.max(...values) : undefined;
};

const buildPreviewTimeRange = (intervalMinutes: number) => {
    const timezoneValue = resolveUserTimezone();
    const to = dayjs.tz(undefined, timezoneValue);
    const from = to.subtract(24, 'hour');
    const interval = Math.max(1, intervalMinutes || 1) * INTERVAL_MINUTE;

    return {
        timeRange: {
            from,
            to,
            previousFrom: from.subtract(24, 'hour'),
            timezone: timezoneValue,
            interval
        },
        from,
        to
    };
};

export const useAlertRulePreview = (
    formData: AlertRuleFormData,
    instances: InstanceTarget[],
    hosts: HostTarget[]
): AlertRulePreviewState => {
    const [tags, setTags] = useState<Tags[]>([]);
    const [disks, setDisks] = useState<HostDisk[]>([]);
    const [selectedEntityId, setSelectedEntityId] = useState<number | string | undefined>();
    const [seriesByEntityId, setSeriesByEntityId] = useState<Record<string, AlertRulePreviewPoint[]>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (formData.entitytype !== 'datasource') {
            setTags([]);
            return;
        }

        let cancelled = false;
        void TagApi.getAllRawTags().then(result => {
            if (!cancelled) {
                setTags(result);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [formData.entitytype]);

    // Volumes on the hosts in scope. Fetched here, like the tags above, so the preview
    // resolves the rule's (host, disk) pairs from formData alone.
    useEffect(() => {
        if (formData.entitytype !== 'disk') {
            setDisks([]);
            return;
        }

        let cancelled = false;
        void DiskApi.getDisks(formData.host).then(result => {
            if (!cancelled) {
                setDisks(result);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [formData.entitytype, formData.host]);

    const matchedEntities = useMemo(
        () => getAlertRulePreviewEntities(formData, instances, hosts, tags, disks),
        [
            formData.entitytype,
            formData.host,
            formData.datasource,
            formData.tag,
            formData.disk,
            instances,
            hosts,
            tags,
            disks
        ]
    );

    useEffect(() => {
        if (matchedEntities.length === 0) {
            setSelectedEntityId(undefined);
            return;
        }

        if (!selectedEntityId || !matchedEntities.some(entity => entity.id === selectedEntityId)) {
            setSelectedEntityId(matchedEntities[0].id);
        }
    }, [matchedEntities, selectedEntityId]);

    const { timeRange, from, to } = useMemo(
        () => buildPreviewTimeRange(formData.evaluationwindowminutes),
        [formData.evaluationwindowminutes]
    );

    useEffect(() => {
        if (!formData.statistic || matchedEntities.length === 0) {
            setSeriesByEntityId({});
            setError(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const debounce = window.setTimeout(() => {
            const loadPreview = async () => {
                setLoading(true);
                setError(null);

                try {
                    const query = buildApiQuery(timeRange);
                    const responses = await Promise.allSettled(matchedEntities.map(async entity => {
                        let response;

                        if (formData.entitytype === 'disk') {
                            response = await DiskApi.getDiskStatisticTime(query, entity.hostId as number, entity.disk as string);
                        } else if (formData.entitytype === 'host') {
                            response = await HostApi.getHostStatisticV2Time(query, [entity.id as number]);
                        } else if (formData.metrictype === 'activity') {
                            const activityResponse = await ActivityApi.getTime(query, entity.id as number, `&statistic=${encodeURIComponent(formData.statistic)}`);
                            response = activityResponse.data;
                        } else {
                            const statisticResponse = await StatisticApi.getTime(query, entity.id as number, formData.statistic);
                            response = statisticResponse.data;
                        }

                        const points = (response ?? [])
                            .filter((row: any) => row?.timeslice)
                            .map((row: any) => ({
                                x: getConvertedTimeToUTC(row),
                                y: getAlertRulePreviewRowValue(row, formData)
                            }));

                        return { entityId: String(entity.id), points };
                    }));

                    if (cancelled) return;

                    const nextSeriesByEntityId: Record<string, AlertRulePreviewPoint[]> = {};
                    let failedCount = 0;

                    responses.forEach(result => {
                        if (result.status === 'fulfilled') {
                            nextSeriesByEntityId[result.value.entityId] = result.value.points;
                        } else {
                            failedCount += 1;
                            console.error('Failed to load alert rule preview entity.', result.reason);
                        }
                    });

                    setSeriesByEntityId(nextSeriesByEntityId);
                    setError(failedCount > 0 ? 'Some preview data could not be loaded.' : null);
                } catch (previewError) {
                    if (!cancelled) {
                        console.error('Failed to load alert rule preview.', previewError);
                        setSeriesByEntityId({});
                        setError('Preview data could not be loaded.');
                    }
                } finally {
                    if (!cancelled) {
                        setLoading(false);
                    }
                }
            };

            void loadPreview();
        }, 350);

        return () => {
            cancelled = true;
            window.clearTimeout(debounce);
        };
    }, [
        formData.entitytype,
        formData.metrictype,
        formData.statistic,
        formData.aggregation,
        matchedEntities,
        timeRange
    ]);

    const matchedEntitiesWithValues = useMemo(() => {
        return matchedEntities.map(entity => ({
            ...entity,
            lastValue: getLastPointValue(seriesByEntityId[String(entity.id)] ?? []),
            maxValue: getMaxPointValue(seriesByEntityId[String(entity.id)] ?? []),
            severityCounts: getAlertRuleTriggeredCounts(
                (seriesByEntityId[String(entity.id)] ?? []).map(point => point.y),
                formData.thresholdarray,
                formData.decreasing
            )
        }));
    }, [matchedEntities, seriesByEntityId, formData.thresholdarray, formData.decreasing]);

    const selectedEntityWithValue = matchedEntitiesWithValues.find(entity => entity.id === selectedEntityId);
    const series = selectedEntityId ? (seriesByEntityId[String(selectedEntityId)] ?? []) : [];
    const entityType: AlertRulePreviewEntityType = formData.entitytype === 'host'
        ? 'host'
        : formData.entitytype === 'disk'
            ? 'disk'
            : 'datasource';

    const emptyReason = !formData.statistic
        ? 'Select a metric to preview matching data.'
        : matchedEntitiesWithValues.length === 0
            ? `No matching ${ENTITY_LABELS[entityType]} found.`
            : selectedEntityWithValue && series.length === 0 && !loading && !error
                ? 'No preview data found for the selected entity over the last 24 hours.'
                : null;

    return {
        entityType,
        matchedEntities: matchedEntitiesWithValues,
        selectedEntityId,
        selectedEntity: selectedEntityWithValue,
        setSelectedEntityId,
        series,
        loading,
        error,
        emptyReason,
        previewFrom: from,
        previewTo: to
    };
};
